import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Valor técnico com cópia.
 *
 * Chave de licença, identificador de tenant e fingerprint de agente existem
 * para serem colados em outro lugar — num e-mail para o cliente, num comando,
 * num ticket. Digitar à mão uma chave de 25 caracteres é onde nasce o chamado
 * de "a licença não funciona".
 *
 * A confirmação é o próprio rótulo do botão trocando por dois segundos. Um
 * toast para isto seria barulho num gesto que o operador repete o dia todo.
 */
export function CopyValue({
  value,
  /** Texto exibido, quando difere do que é copiado (um id abreviado, p. ex.). */
  display,
  label,
  className = '',
}: {
  value: string
  display?: string
  label: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  const copy = useCallback(async () => {
    try {
      // Exige contexto seguro (https ou localhost). Fora disso a API não existe,
      // e dizer "copiado" sem ter copiado é pior do que não oferecer.
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setFailed(false)
    } catch {
      setCopied(false)
      setFailed(true)
    }

    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      setCopied(false)
      setFailed(false)
    }, 2_000)
  }, [value])

  return (
    <span className={`inline-flex min-w-0 items-center gap-1.5 ${className}`}>
      <span className="truncate font-mono text-fg">{display ?? value}</span>
      <button
        type="button"
        onClick={() => void copy()}
        title={`Copiar ${label}`}
        aria-label={`Copiar ${label}`}
        className="shrink-0 border border-line-strong px-1 py-px text-micro uppercase tracking-label text-fg-muted hover:bg-inset hover:text-fg"
      >
        {copied ? 'copiado' : failed ? 'falhou' : 'copiar'}
      </button>
    </span>
  )
}

/** Identificador longo abreviado, com o valor inteiro no title e na cópia. */
export function ShortId({ id, label }: { id: string; label: string }) {
  return <CopyValue value={id} display={id.slice(0, 8)} label={label} />
}
